/**
 * Where a job's money is — the Billing column. Not a status anyone sets: it is
 * read from the invoice that bills the job, or, before there is one, from
 * whether the location pays a monthly rate (docs/majik/PROTOTYPE-DECISIONS.md
 * → Jobs: "billing state is an indicator, not a button").
 */
import type { BillingItem } from '../clients/client.ts'
import { type Invoice, isOverdue } from '../invoices/invoice.ts'
import { money } from '../shared/money.ts'
import { dayKey, daysBetween, shortDay } from '../shared/zoned.ts'
import type { Job } from './job.ts'

export const billingStates = [
  'ready',
  'awaiting',
  'overdue',
  'draft',
  'paid',
  'written_off',
  'monthly',
  'after_visit',
  'none',
] as const
export type BillingState = (typeof billingStates)[number]

export const billingLabels: Record<BillingState, string> = {
  ready: 'Ready to bill',
  awaiting: 'Awaiting payment',
  overdue: 'Overdue',
  draft: 'On a draft invoice',
  paid: 'Paid',
  written_off: 'Written off',
  monthly: 'In monthly rate',
  after_visit: 'Billed after visit',
  none: 'Not billed',
}

/** States that are something to act on or a settled result get a pill; the rest are plain text. */
export const billingIsPill: Record<BillingState, boolean> = {
  ready: true,
  awaiting: true,
  overdue: true,
  draft: true,
  paid: true,
  written_off: true,
  monthly: false,
  after_visit: false,
  none: false,
}

export interface BillingContext {
  /** The invoice holding a line for this job, if any. */
  invoice: Invoice | null
  /** The location's active monthly billing items. */
  monthlyItems: readonly BillingItem[]
  now: string
  overdueAfterDays: number
  timeZone: string
}

export function billingState(job: Job, ctx: BillingContext): BillingState {
  if (job.status === 'cancelled') return 'none'
  const { invoice } = ctx
  if (invoice) {
    if (invoice.status === 'paid') return 'paid'
    // A voided invoice bills nothing (the repository leaves it out); a written-off one did bill the visit.
    if (invoice.status === 'uncollectible') return 'written_off'
    if (invoice.status === 'sent') {
      return isOverdue(invoice, ctx.now, ctx.overdueAfterDays) ? 'overdue' : 'awaiting'
    }
    return 'draft'
  }
  const monthly = ctx.monthlyItems.length > 0
  if (job.status === 'scheduled') return monthly ? 'monthly' : 'after_visit'
  if (job.billAmount && job.billAmount > 0) return 'ready'
  return monthly ? 'monthly' : 'none'
}

/** The job page's one line under Billing → Invoice. */
export function billingText(job: Job, ctx: BillingContext): string {
  const state = billingState(job, ctx)
  const { invoice, timeZone } = ctx
  const number = invoice?.invoiceNumber ?? invoice?.name ?? ''
  switch (state) {
    case 'paid':
      return invoice?.paidAt
        ? `Invoice ${number}, paid ${shortDay(invoice.paidAt, timeZone)}`
        : `Invoice ${number}, paid`
    case 'written_off':
      return `Invoice ${number}, written off as uncollectible`
    case 'awaiting':
      return invoice?.lastSentAt
        ? `Invoice ${number}, sent ${shortDay(invoice.lastSentAt, timeZone)}, not paid yet`
        : `Invoice ${number}, not paid yet`
    case 'overdue': {
      const sent = invoice?.lastSentAt
      if (!sent) return `Invoice ${number}, overdue`
      const days = daysBetween(dayKey(sent, timeZone), dayKey(ctx.now, timeZone))
      return `Invoice ${number}, sent ${shortDay(sent, timeZone)}, unpaid for ${days} days`
    }
    case 'draft':
      return `Draft invoice “${invoice?.name ?? ''}”, not sent yet`
    case 'ready':
      return 'Not on an invoice yet'
    case 'monthly': {
      const total = ctx.monthlyItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
      const names = ctx.monthlyItems.map((item) => item.itemName).join(', ')
      return `Covered by the monthly rate: ${names}, ${money(total)} a month`
    }
    case 'after_visit':
      return 'Billed after the visit'
    case 'none':
      return job.status === 'cancelled' ? 'Cancelled, so not billed' : 'Nothing to bill'
  }
}
